import React, { useState } from "react";
import axios from "axios";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Send, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactForm() {
  const { t, lang } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        service: form.service || null,
        message: form.message,
        language: lang,
      });
      toast.success(t.contact.form.success);
      setDone(true);
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error(t.contact.form.error);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        className="rounded-xl bg-brand-surface border border-brand-line p-10 text-center"
        data-testid="contact-success"
      >
        <CheckCircle2 size={56} className="mx-auto text-brand-accent" />
        <h3 className="mt-5 font-serif text-2xl text-brand-primary">
          {t.contact.form.success}
        </h3>
        <Button
          variant="outline"
          onClick={() => setDone(false)}
          className="mt-6 rounded-full"
          data-testid="contact-send-another"
        >
          {lang === "es" ? "Enviar otro mensaje" : "Send another message"}
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="rounded-xl bg-brand-surface border border-brand-line p-7 sm:p-10 space-y-5"
      data-testid="contact-form"
    >
      <div className="grid sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label htmlFor="name">{t.contact.form.name}</Label>
          <Input
            id="name"
            required
            value={form.name}
            onChange={update("name")}
            data-testid="contact-input-name"
            className="rounded-md"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">{t.contact.form.email}</Label>
          <Input
            id="email"
            type="email"
            required
            value={form.email}
            onChange={update("email")}
            data-testid="contact-input-email"
            className="rounded-md"
          />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label htmlFor="phone">{t.contact.form.phone}</Label>
          <Input
            id="phone"
            value={form.phone}
            onChange={update("phone")}
            data-testid="contact-input-phone"
            className="rounded-md"
          />
        </div>
        <div className="space-y-2">
          <Label>{t.contact.form.service}</Label>
          <Select
            value={form.service}
            onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}
          >
            <SelectTrigger data-testid="contact-input-service" className="rounded-md">
              <SelectValue placeholder={t.contact.form.service_placeholder} />
            </SelectTrigger>
            <SelectContent>
              {t.services.items.map((s) => (
                <SelectItem key={s.key} value={s.key} data-testid={`service-option-${s.key}`}>
                  {s.name}
                </SelectItem>
              ))}
              <SelectItem value="other">{lang === "es" ? "Otro" : "Other"}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="message">{t.contact.form.message}</Label>
        <Textarea
          id="message"
          required
          rows={5}
          value={form.message}
          onChange={update("message")}
          data-testid="contact-input-message"
          className="rounded-md resize-none"
        />
      </div>

      <Button
        type="submit"
        disabled={loading}
        data-testid="contact-submit-btn"
        className="rounded-full bg-brand-primary text-brand-surface hover:bg-brand-secondary px-7 py-6 text-base font-semibold inline-flex items-center gap-2"
      >
        {loading ? t.contact.form.sending : t.contact.form.submit}
        {!loading && <Send size={16} />}
      </Button>
    </form>
  );
}
